import componentStyles from './betCard.module.css';
import globalStyles from '../globals.module.css';
import React from 'react';
import eventBus from '../../service/eventBus';
import NoImage from '../global/noImage';
import { 
    EVENT_TRIGGER_IN_DEPTH_BET_CARD, 
    EVENT_UPDATE_IN_DEPTH_BET_CARD, 
    EVENT_FEED_LOCKED,
    EVENT_UPDATE_ACTION_BAR_VIEW,
    EVENT_UPDATE_IN_DEPTH_BET_ID,
    EVENT_TRIGGER_REGISTRATION_MODAL
} from '../../utils/constants';
import SessionService from '../../service/sessionService';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_BET_CARD_CLICKED } from '../../utils/analyticsConstants';

const BetCard = ({ betCard, prevView }) => {
    const isPGA = ['pga', 'pga_tour', 'golf'].includes(String(betCard?.sportType || '').toLowerCase());
    const hasScoutingReport = !!(betCard?.scouting_report && typeof betCard.scouting_report === 'object' && Object.keys(betCard.scouting_report).length > 0);
    const hasPropBetReport = !!(betCard?.prop_bet_report && typeof betCard.prop_bet_report === 'object' && Object.keys(betCard.prop_bet_report).length > 0);
    const hasAnalysisContent = hasScoutingReport || hasPropBetReport;
    const shouldDisableAnalysis = !isPGA && !hasAnalysisContent;

    const handleClick = () => {
        if (shouldDisableAnalysis) {
            return;
        }

        const isAuthenticated = SessionService.getInstance().isAuthenticated();
        AnalyticsService.getInstance().track(EVENT_BET_CARD_CLICKED, {
            betId: String(betCard?.id ?? ''),
            sportType: betCard?.sportType ?? '',
            prevView: prevView ?? 'feed',
            openedInDepth: isAuthenticated,
            registrationModalShown: !isAuthenticated
        });

        // Check if user is authenticated before proceeding
        if (!isAuthenticated) {
            // Emit event to show registration modal for unauthenticated users
            eventBus.emit(EVENT_TRIGGER_REGISTRATION_MODAL, true);
            return; // Don't proceed with analysis
        }
        
        console.log(`Event ${EVENT_TRIGGER_IN_DEPTH_BET_CARD} triggered with value`, true);
        SessionService.getInstance().setPrevView(prevView);
        eventBus.emit(EVENT_TRIGGER_IN_DEPTH_BET_CARD, true);        
        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'inDepthBetCard');
        setTimeout(() => {
            eventBus.emit(EVENT_UPDATE_IN_DEPTH_BET_CARD, betCard);
            eventBus.emit(EVENT_UPDATE_IN_DEPTH_BET_ID, betCard.id);
        }, 500);
    }

    // Convert decimal odds to American odds
    const decimalToAmerican = (decimal) => {
        if (decimal >= 2) {
            return Math.round((decimal - 1) * 100);
        } else {
            return Math.round(-100 / (decimal - 1));
        }
    };

    // Get confidence badge color
    const getConfidenceColor = (confidence) => {
        switch (confidence?.toLowerCase()) {
            case 'high':
                return '#00ff00'; // Green
            case 'medium':
                return '#808000'; // Olive
            case 'low':
                return '#ff0000'; // Red
            default:
                return '#808000'; // Default to olive
        }
    };

    // Get confidence badge text
    const getConfidenceText = (confidence) => {
        return confidence?.toUpperCase() || 'MEDIUM';
    };

    const renderFullAnalysisButton = () => {
        if (shouldDisableAnalysis) {
            return null;
        }

        return (
            <button className={componentStyles.fullAnalysisButton} onClick={handleClick}>
                {SessionService.getInstance().isAuthenticated() ? 'TAP FOR FULL ANALYSIS' : 'UNLOCK THE FULL ANALYSIS'}
            </button>
        );
    };

    const renderInsightsSection = () => (
        <div className={componentStyles.insightsSection}>
            {hasScoutingReport && betCard['scouting_report']['featuredProp'] ? (
                <div className={componentStyles.smallInsights}>
                    <div className={componentStyles.insightItem}>
                        <span className={componentStyles.smallInsightsLabel}>TOP PROP:</span>
                        <span className={componentStyles.smallInsightsValue}>
                            {betCard['scouting_report']['featuredProp']['propBet']}
                        </span>
                    </div>
                    <div className={componentStyles.insightItem}>
                        <span className={componentStyles.smallInsightsLabel}>ADVANTAGES:</span>
                        <span className={componentStyles.smallInsightsValue}>
                            {betCard['scouting_report']['featuredProp']['propBetExplanation']}
                        </span>
                    </div>
                </div>
            ) : null}
        </div>
    );

    const renderUnavailableAnalysisMessage = () => (
        <div className={componentStyles.unavailableAnalysisMessage}>
            Scouting report is not ready yet.
        </div>
    );

    const pgaBetCard = () => {
        const matchupDisplay = betCard.matchup || betCard.matchUp || betCard.eventName || 'N/A';

        return (
            <div className={componentStyles.card + ' ' + globalStyles.slight3dEffect}>
                <div className={componentStyles.betCardHeader}>
                    <div className={componentStyles.eventInfo}>
                        <div className={componentStyles.eventStatusContainer}>
                            <span className={componentStyles.sportBadge}>
                                <img src={`https://placehold.co/85x20?text=${betCard.sportType}`} alt={betCard.sportType} />
                            </span>
                            <span className={componentStyles.matchupName}>{matchupDisplay}</span>
                        </div>
                        <div className={componentStyles.eventStatusContainer}>
                            <span className={componentStyles.eventStatusUpcoming + ' ' + componentStyles.betCardStatus}>
                                Upcoming
                            </span>
                            <span className={componentStyles.eventDate}>
                                {betCard.upcomingEventDt ? betCard.upcomingEventDt : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                {!shouldDisableAnalysis && renderInsightsSection()}

                <div className={componentStyles.cardBottom}>
                    {renderFullAnalysisButton()}
                </div>
                {shouldDisableAnalysis && renderUnavailableAnalysisMessage()}
            </div>
        );
    };

    const unifiedPropBetCard = () => {
        const isMMA = betCard.betType === 'fighter_prop';
        
        // Helper to safely get nested property and fallback to 'N/A'
        const safeGet = (obj, path, fallback = 'N/A') => {
            return path.reduce((acc, key) => (acc && acc[key] !== undefined && acc[key] !== null) ? acc[key] : undefined, obj) ?? fallback;
        };

        // Get matchup display name based on sport type
        const getMatchupDisplay = () => {
            if (isMMA) {
                return betCard.fightTitle || 'N/A';
            } else {
                const awayTeamShortName = betCard.awayTeam?.split(' ').pop() || 'N/A';
                const homeTeamShortName = betCard.homeTeam?.split(' ').pop() || 'N/A';
                return `${homeTeamShortName} vs ${awayTeamShortName}`;
            }
        };

        // Process odds based on sport type
        let mlOdds, spreadOdds, totalOdds;

        if (isMMA) {
            // MMA odds processing
            const h2hOdds = (betCard.h2hOdds && betCard.h2hOdds[0] && Array.isArray(betCard.h2hOdds[0].odds))
                ? betCard.h2hOdds[0].odds.map(decimalToAmerican)
                : [null, null];

            const spreads = (betCard.spreads && betCard.spreads[0] && Array.isArray(betCard.spreads[0].odds))
                ? betCard.spreads[0].odds.map(decimalToAmerican)
                : [null, null];

            const totals = (betCard.totals && betCard.totals[0] && Array.isArray(betCard.totals[0].odds))
                ? betCard.totals[0].odds.map(decimalToAmerican)
                : [null, null];

            mlOdds = { side: 'N/A', price: h2hOdds[0] || 'N/A' };
            spreadOdds = { side: 'N/A', points: 'N/A', price: spreads[0] || 'N/A' };
            totalOdds = { side: 'N/A', points: 'N/A', price: totals[0] || 'N/A' };
        } else {
            // Team prop odds processing
            const tobBets = betCard && betCard.favoredOdds ? betCard.favoredOdds : {};

            const mlOddsSideRaw = safeGet(tobBets, ['h2h', 'side']);
            const mlOddsSide = typeof mlOddsSideRaw === 'string' ? mlOddsSideRaw.split(' ').pop() : 'N/A';
            const mlOddsPriceRaw = safeGet(tobBets, ['h2h', 'odds'], null);
            const mlOddsPrice = mlOddsPriceRaw !== null && mlOddsPriceRaw !== undefined ? decimalToAmerican(mlOddsPriceRaw) : 'N/A';

            const spreadOddsSideRaw = safeGet(tobBets, ['spread', 'side']);
            const spreadOddsSide = typeof spreadOddsSideRaw === 'string' ? spreadOddsSideRaw.split(' ').pop() : 'N/A';
            const spreadOddsPoints = safeGet(tobBets, ['spread', 'point']);
            const spreadOddsPriceRaw = safeGet(tobBets, ['spread', 'odds'], null);
            const spreadOddsPrice = spreadOddsPriceRaw !== null && spreadOddsPriceRaw !== undefined ? decimalToAmerican(spreadOddsPriceRaw) : 'N/A';

            const totalOddsSide = safeGet(tobBets, ['total', 'side']);
            const totalOddsPoints = safeGet(tobBets, ['total', 'point']);
            const totalOddsPriceRaw = safeGet(tobBets, ['total', 'odds'], null);
            const totalOddsPrice = totalOddsPriceRaw !== null && totalOddsPriceRaw !== undefined ? decimalToAmerican(totalOddsPriceRaw) : 'N/A';

            mlOdds = { side: mlOddsSide, price: mlOddsPrice };
            spreadOdds = { side: spreadOddsSide, points: spreadOddsPoints, price: spreadOddsPrice };
            totalOdds = { side: totalOddsSide, points: totalOddsPoints, price: totalOddsPrice };
        }

        return (
            <div className={componentStyles.card + ' ' + globalStyles.slight3dEffect}>
                {/* Header Section - High Priority */}
                <div className={componentStyles.betCardHeader}>
                    <div className={componentStyles.eventInfo}>
                        <div className={componentStyles.eventStatusContainer}>
                            <span className={componentStyles.sportBadge}>
                                <img src={`https://placehold.co/85x20?text=${betCard.sportType}`} alt={betCard.sportType} />
                            </span>
                            <span className={componentStyles.matchupName}>{getMatchupDisplay()}</span>
                        </div>
                        <div className={componentStyles.eventStatusContainer}>
                            <span className={componentStyles.eventStatusUpcoming + ' ' + componentStyles.betCardStatus}>
                                Upcoming
                            </span> 
                            <span className={componentStyles.eventDate}>
                                {betCard.upcomingEventDt ? betCard.upcomingEventDt : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                {!isMMA && (
                    <div className={componentStyles.oddsSection}>
                        <div className={componentStyles.oddsInfo}>
                            <div className={componentStyles.oddsItem}>
                                <span className={componentStyles.oddsLabel}>MONEYLINE</span>
                                <span className={componentStyles.oddsItemValue}>
                                    {mlOdds.side} {mlOdds.price}
                                </span>
                            </div>
                            <div className={componentStyles.oddsItem}>
                                <span className={componentStyles.oddsLabel}>SPREAD</span>
                                <span className={componentStyles.oddsItemValue}>
                                    {spreadOdds.side} {spreadOdds.points > 0 ? `+${spreadOdds.points}` : spreadOdds.points} {spreadOdds.price > 0 ? `+${spreadOdds.price}` : spreadOdds.price}
                                </span>
                            </div>
                            <div className={componentStyles.oddsItem}>
                                <span className={componentStyles.oddsLabel}>TOTALS</span>
                                <span className={componentStyles.oddsItemValue}>
                                    {totalOdds.side} {totalOdds.points} {totalOdds.price}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                
                {!shouldDisableAnalysis && renderInsightsSection()}
                

                {/* Call-to-Action Section - High Priority */}
                <div className={componentStyles.cardBottom}>
                    {renderFullAnalysisButton()}
                </div>
                {shouldDisableAnalysis && renderUnavailableAnalysisMessage()}
            </div>
        )
    }

    const playerPropBetCard = () => {
        return (
            <div className={componentStyles.card + ' ' + globalStyles.slight3dEffect} onClick={handleClick}>
                <div className={componentStyles.playerCardContent}>
                    <div className={componentStyles.cardLeft}>
                        {betCard.imgUrl ? (
                            <img src={betCard.imgUrl} alt={betCard.playerName} className={componentStyles.playerImage} />
                        ) : (
                            <NoImage />
                        )}
                    </div>
                    <div className={componentStyles.cardRight}>
                        <h3 className={componentStyles.playerName}>{betCard.playerName}</h3>
                        <p className={componentStyles.playerMatchup}>{betCard.matchup}</p>
                        <p className={componentStyles.playerEventDate}>
                            {betCard.upcomingEventDt ? betCard.upcomingEventDt : 'N/A'}
                        </p>
                    </div>
                    <div className={componentStyles.cardBottom}>
                        {renderFullAnalysisButton()}
                    </div>
                    {shouldDisableAnalysis && renderUnavailableAnalysisMessage()}
                </div>
            </div>
        )
    }

    const getBetCardType = () => {
        if (isPGA) {
            return pgaBetCard();
        }

        switch(betCard.betType) {
            case 'team_prop':
                return unifiedPropBetCard();
            case 'player_prop':
                return playerPropBetCard();
            case 'fighter_prop':
                return unifiedPropBetCard();
            default:
                return unifiedPropBetCard();
        }
    }

    return getBetCardType();
}

export default BetCard;
